/**
 * mk-cart-upsell.js — passage au palier supérieur depuis le panier tiroir.
 *
 * ============================ HISTORIQUE DES PIÈGES ============================
 * Quatre bugs successifs ici, tous dus à BOGOS qui réagit de façon ASYNCHRONE aux
 * changements de panier. Lire avant de "simplifier".
 *
 * 1. AJOUTER PUIS SUPPRIMER PAR LA CLÉ DU RENDU -> les deux variantes restaient.
 *    La `key` d'une ligne n'est pas stable : Shopify la recalcule dès qu'une
 *    caractéristique change, y compris les remises appliquées.
 *    cf. https://shopify.dev/docs/api/ajax/reference/cart#post--locale-cart-changejs
 *
 * 2. SUPPRIMER PUIS AJOUTER -> les cadeaux disparaissaient de l'affichage.
 *    Le retrait fait rentrer le panier sous un palier : BOGOS supprime ses
 *    cadeaux puis les rajoute après notre add. Rendre dans cette fenêtre
 *    photographie un panier amputé alors que le serveur est correct.
 *
 * 3. ATTENDRE QUE item_count SE STABILISE -> insuffisant. Deux lectures
 *    identiques peuvent tomber PENDANT la fenêtre où les cadeaux sont absents.
 *
 * 4. CLICS RAPIDES ENCHAÎNÉS -> panier incohérent. Le bouton cliqué est bien
 *    neutralisé, mais le rafraîchissement RECRÉE le DOM : un bouton neuf et
 *    actif apparaît alors que BOGOS n'a pas fini de réagir. D'où le verrou
 *    global + la période de garde ci-dessous.
 *
 * ================================ SOLUTION =====================================
 * On AJOUTE d'abord (le panier ne fait que grossir, BOGOS n'a aucune raison de
 * retirer quoi que ce soit), PUIS on relit /cart.js pour retrouver la clé À JOUR
 * de l'ancienne ligne avant de la supprimer.
 *
 * MARQUAGE : la nouvelle ligne reçoit la propriété privée _mk_upsold, sans quoi
 * l'upsell se reproposerait de palier en palier. Elle sert aussi à distinguer
 * l'ancienne ligne lors de la relecture.
 */

/* Verrou partagé par toutes les instances : une seule bascule à la fois. */
const MK_UPSELL = {
  busy: false,
  /** Garde après rendu, le temps que BOGOS termine ses ajouts. */
  cooldownMs: 900,
};

function mkUpsellSyncButtons() {
  document.querySelectorAll('.mk-upsell__btn').forEach((button) => {
    if (MK_UPSELL.busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  });
}

class MkCartUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.mk-upsell__btn');
    if (!this.button) return;

    // Ce bloc peut apparaître pendant qu'une bascule est en cours : on le rend
    // inactif dès son insertion plutôt que d'attendre un clic pour le refuser.
    if (MK_UPSELL.busy) this.button.setAttribute('aria-busy', 'true');

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.upgrade();
    });
  }

  cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  postJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
  }

  async getCart() {
    const response = await fetch(`${this.cartUrl()}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  /**
   * Retrouve la clé courante de la ligne à remplacer : même variante qu'avant,
   * et SANS la marque _mk_upsold (sinon on risquerait de supprimer une ligne
   * issue d'un upsell précédent portant la même variante).
   */
  findPreviousLineKey(cart, previousVariantId) {
    const wanted = String(previousVariantId);
    const line = (cart.items || []).find((item) => {
      const sameVariant = String(item.variant_id) === wanted;
      const upsold = item.properties && item.properties._mk_upsold;
      return sameVariant && !upsold;
    });
    return line ? line.key : null;
  }

  async upgrade() {
    if (MK_UPSELL.busy) return;

    const variantId = this.dataset.variantId;
    const previousVariantId = this.dataset.previousVariantId;
    if (!variantId || !previousVariantId) return;

    MK_UPSELL.busy = true;
    mkUpsellSyncButtons();

    try {
      // 1. Ajout du palier supérieur. Le panier ne fait que grossir.
      const added = await this.postJson('/cart/add.js', {
        id: variantId,
        quantity: 1,
        properties: { _mk_upsold: '1' },
      });
      if (!added.ok) throw new Error(`add.js ${added.status}`);

      // 2. Relecture pour obtenir la clé À JOUR de l'ancienne ligne.
      const cart = await this.getCart();
      const staleKey = this.findPreviousLineKey(cart, previousVariantId);

      // 3. Retrait de l'ancienne ligne. Si elle est introuvable, on ne supprime
      //    rien plutôt que de risquer de retirer la mauvaise.
      if (staleKey) {
        const removed = await this.postJson('/cart/change.js', { id: staleKey, quantity: 0 });
        if (!removed.ok) throw new Error(`change.js ${removed.status}`);
      } else {
        console.warn('[mk-upsell] ancienne ligne introuvable, non supprimée');
      }

      await this.refresh();
    } catch (error) {
      console.error('[mk-upsell]', error);
      await this.refresh();
    } finally {
      // Le DOM vient d'être remplacé : les boutons rendus pendant la garde sont
      // déjà inactifs via connectedCallback, on les réactive tous à l'échéance.
      setTimeout(() => {
        MK_UPSELL.busy = false;
        mkUpsellSyncButtons();
      }, MK_UPSELL.cooldownMs);
    }
  }

  /**
   * Re-rendu complet du tiroir. openCartDrawer() (mk-legacy-atc.js) remplace
   * l'intégralité de #CartDrawer, rebranche l'overlay et purge `is-empty`.
   *
   * ⚠️ Ne pas revenir à un remplacement partiel (.mk-progress /
   * cart-drawer-items / .cart-drawer__footer) : ces trois zones laissent intacts
   * le bloc .drawer__inner-empty et la classe `is-empty` sur <cart-drawer>, et
   * le tiroir reste affiché vide alors que le pied montre les bons totaux.
   */
  async refresh() {
    if (
      window.Shopify &&
      Shopify.theme &&
      Shopify.theme.jsAjaxCart &&
      typeof Shopify.theme.jsAjaxCart.openCartDrawer === 'function'
    ) {
      Shopify.theme.jsAjaxCart.openCartDrawer();
      return;
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const response = await fetch(`${this.cartUrl()}?section_id=cart-drawer`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const source = html.querySelector('#CartDrawer');
    const target = document.querySelector('#CartDrawer');
    if (source && target) target.innerHTML = source.innerHTML;

    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));

    drawer.classList.remove('is-empty');
  }
}

if (!customElements.get('mk-cart-upsell')) {
  customElements.define('mk-cart-upsell', MkCartUpsell);
}
